// PATH: src/pages/DashboardPage.tsx — page d'accueil de la direction.
// Vue d'ensemble : effectifs, encaissement de la période courante, assiduité du
// jour, prochaines évaluations, alertes. Chaque bloc pointe vers son module.
//
// Dégradation gracieuse : les blocs réservés à la direction (dashboard,
// paiements) se masquent sur 403 au lieu de bloquer la page — un profil non
// direction garde une page d'accueil avec ses raccourcis.
import React, { useEffect, useState } from "react";
import { Button, Card, Col, List, Progress, Row, Space, Tag, Typography } from "antd";
import {
  TeamOutlined, DollarOutlined, CalendarOutlined, WarningOutlined, RightOutlined,
  RocketOutlined, CheckCircleTwoTone, CheckCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import PageHeader from "../components/PageHeader";
import { useDashboardApi, type DashboardSummary } from "../api/dashboard";
import { useSchoolApi } from "../api/school";
import { usePedagogyApi } from "../api/pedagogy";
import { useProfileApi } from "../api/profile";
import { usePaymentsApi, type PaymentPeriod, type PaymentRow } from "../api/payments";
import { useRecentStudents } from "../hooks/useRecentStudents";
import { useCountUp } from "../hooks/useCountUp";
import { COLORS } from "../theme";

const formatAmount = (v: number) => `${new Intl.NumberFormat("fr-MA").format(v)} DH`;

// Encaissement de la période courante, agrégé côté client à partir des mêmes
// lignes que l'écran Paiements — garantit des chiffres identiques.
interface PaySummary {
  label: string;
  expected: number;
  collected: number;
  outstanding: number;
  rate: number;
  unpaidCount: number;
  unassigned: number;
}

// pickCurrentPeriod : la période dont l'intervalle contient aujourd'hui ; à
// défaut, la dernière période déjà commencée ; sinon la première.
const pickCurrentPeriod = (ps: PaymentPeriod[]): PaymentPeriod | undefined => {
  if (ps.length === 0) return undefined;
  const t = dayjs().startOf("day").valueOf();
  const covering = ps.find(
    (p) => p.start_date && p.end_date &&
      dayjs(p.start_date).startOf("day").valueOf() <= t &&
      t <= dayjs(p.end_date).startOf("day").valueOf()
  );
  if (covering) return covering;
  const started = ps
    .filter((p) => p.start_date && dayjs(p.start_date).startOf("day").valueOf() <= t)
    .sort((a, b) => dayjs(b.start_date).valueOf() - dayjs(a.start_date).valueOf());
  return started[0] ?? ps[0];
};

const KIND_LABEL: Record<string, string> = { EXAMEN: "Examen", CONTROLE_CONTINU: "Contrôle" };

interface SetupStep { key: string; label: string; hint: string; done: boolean; to: string }

/**
 * SetupChecklist — démarrage d'un établissement neuf. Les modules ne
 * fonctionnent qu'une fois posées, DANS L'ORDRE, les briques de configuration
 * (année → niveaux → classes → matières → trimestres → tarifs → fiche). Cet
 * ordre est aujourd'hui éclaté sur quatre écrans : la carte le rassemble et
 * guide pas à pas. Détection 100 % côté client, à partir des listes existantes ;
 * la carte s'efface d'elle-même une fois tout en place.
 */
const SetupChecklist: React.FC = () => {
  const school = useSchoolApi();
  const pedagogy = usePedagogyApi();
  const payApi = usePaymentsApi();
  const profileApi = useProfileApi();
  const navigate = useNavigate();
  const [steps, setSteps] = useState<SetupStep[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ys = await school.listYears().catch(() => []);
      const yearId = ys[0]?.id;
      const [levels, classes, subjects, terms, periods, profile, students] = await Promise.all([
        school.listLevels().catch(() => []),
        yearId ? school.listClasses(yearId).catch(() => []) : Promise.resolve([]),
        pedagogy.listSubjects().catch(() => []),
        yearId ? pedagogy.listTerms(yearId).catch(() => []) : Promise.resolve([]),
        yearId ? payApi.listPeriods(yearId).catch(() => []) : Promise.resolve([]),
        profileApi.get().catch(() => null),
        school.listStudents({ page_size: 1 }).then((r) => r.total).catch(() => 0),
      ]);
      if (cancelled) return;
      setSteps([
        { key: "year", label: "Année scolaire", hint: "Créez l'année en cours", done: ys.length > 0, to: "/referentiels" },
        { key: "levels", label: "Niveaux & filières", hint: "Ex. : 1AP, 2AP, Tronc commun…", done: levels.length > 0, to: "/referentiels" },
        { key: "classes", label: "Classes", hint: "Rattachées à un niveau", done: classes.length > 0, to: "/referentiels" },
        { key: "subjects", label: "Matières", hint: "Pour l'emploi du temps et les notes", done: subjects.length > 0, to: "/admin-pedagogique" },
        { key: "terms", label: "Trimestres", hint: "Pour les bulletins", done: terms.length > 0, to: "/admin-pedagogique" },
        { key: "periods", label: "Périodes de facturation", hint: "Et les tarifs par classe", done: periods.length > 0, to: "/paiements" },
        { key: "profile", label: "Fiche établissement", hint: "Nom, logo, mentions légales — pour les documents", done: !!profile?.legal_name, to: "/ecole" },
        { key: "students", label: "Premier élève", hint: "Inscrivez ou importez vos élèves", done: students > 0, to: "/eleves" },
      ]);
    })();
    return () => { cancelled = true; };
  }, [school, pedagogy, payApi, profileApi]);

  if (!steps) return null;
  const done = steps.filter((s) => s.done).length;
  // Configuration terminée : la carte disparaît, la page d'accueil respire.
  if (done === steps.length) return null;

  return (
    <Card
      title={<Space><RocketOutlined /> Démarrage — configurez votre établissement</Space>}
      extra={<Typography.Text type="secondary">{done}/{steps.length} terminé</Typography.Text>}
      style={{ marginBottom: 16 }}
    >
      <Progress percent={Math.round((done / steps.length) * 100)} showInfo={false} style={{ marginBottom: 4 }} />
      <List
        dataSource={steps}
        renderItem={(s) => (
          <List.Item
            actions={[
              s.done ? (
                <Typography.Text type="secondary" key="d">Fait</Typography.Text>
              ) : (
                <Button size="small" type="link" key="go" onClick={() => navigate(s.to)}>Configurer</Button>
              ),
            ]}
          >
            <List.Item.Meta
              avatar={
                s.done ? (
                  <CheckCircleTwoTone twoToneColor={COLORS.accent} style={{ fontSize: 18 }} />
                ) : (
                  <span style={{
                    display: "inline-block", width: 18, height: 18, borderRadius: "50%",
                    border: "2px solid var(--app-border)",
                  }} />
                )
              }
              title={
                <span style={{ textDecoration: s.done ? "line-through" : undefined, color: s.done ? "var(--app-muted)" : undefined }}>
                  {s.label}
                </span>
              }
              description={s.hint}
            />
          </List.Item>
        )}
      />
    </Card>
  );
};

// ── Indicateurs ──────────────────────────────────────────────────────────────

type Tone = "info" | "success" | "danger" | "warning" | "neutral";

// Teintes en rgba plutôt qu'en aplat : la même valeur tient sur une surface
// claire comme sur une surface sombre, sans second jeu de couleurs.
const TONES: Record<Tone, { fg: string; bg: string }> = {
  info: { fg: COLORS.primary, bg: "rgba(37,99,235,0.10)" },
  success: { fg: COLORS.accent, bg: "rgba(5,150,105,0.10)" },
  danger: { fg: COLORS.danger, bg: "rgba(220,38,38,0.10)" },
  warning: { fg: COLORS.warning, bg: "rgba(217,119,6,0.10)" },
  neutral: { fg: "var(--app-muted)", bg: "var(--row-highlight)" },
};

/** Chiffre qui monte jusqu'à sa valeur (cf. useCountUp). */
const CountUp: React.FC<{ value: number; format?: (n: number) => string }> = ({ value, format }) => {
  const shown = useCountUp(value);
  const rounded = Math.round(shown);
  return <>{format ? format(rounded) : new Intl.NumberFormat("fr-MA").format(rounded)}</>;
};

/**
 * StatCard — un indicateur qui dit son état avant qu'on lise son chiffre.
 *
 * La couleur n'est pas décorative : la pastille prend la teinte du sens
 * (vert = rien à faire, rouge = à traiter), et le liseré du haut ne s'allume
 * QUE lorsque le chiffre appelle une action. Une carte au repos reste neutre —
 * sans quoi tout est coloré et plus rien ne ressort.
 */
const StatCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  tone?: Tone;
  /** Le chiffre appelle une action : liseré coloré en haut de la carte. */
  alert?: boolean;
  footer?: React.ReactNode;
  to: string;
  /** Décalage d'entrée, pour que les quatre cartes arrivent en cascade. */
  delay?: number;
  loading?: boolean;
}> = ({ icon, label, value, tone = "neutral", alert, footer, to, delay = 0, loading }) => {
  const navigate = useNavigate();
  const t = TONES[tone];
  return (
    <Card
      className="stat-card app-fade-in"
      style={{ animationDelay: `${delay}ms` }}
      styles={{ body: { padding: 18 } }}
      loading={loading}
      onClick={() => navigate(to)}
    >
      {alert && <span className="stat-accent" style={{ background: t.fg }} />}
      <div className="stat-head">
        <span className="stat-icon" style={{ background: t.bg, color: t.fg }}>
          {icon}
        </span>
        <Typography.Text type="secondary" style={{ fontSize: 13, fontWeight: 500 }}>
          {label}
        </Typography.Text>
      </div>
      <div className="stat-value" style={{ color: alert ? t.fg : undefined }}>
        {value}
      </div>
      {footer && <div className="stat-foot">{footer}</div>}
    </Card>
  );
};

/**
 * EmptyState — un écran vide est une invitation à agir, pas un constat. Le
 * pictogramme gris d'antd occupait 240px pour dire « rien » ; celui-ci dit
 * aussi quoi faire ensuite.
 */
const EmptyState: React.FC<{
  icon: React.ReactNode;
  text: string;
  tone?: Tone;
  action?: React.ReactNode;
}> = ({ icon, text, tone = "neutral", action }) => {
  const t = TONES[tone];
  return (
    <div style={{ padding: "26px 16px", textAlign: "center" }}>
      <span className="empty-mark" style={{ background: t.bg, color: t.fg }}>
        {icon}
      </span>
      <Typography.Text type="secondary" style={{ display: "block", marginBottom: action ? 10 : 0 }}>
        {text}
      </Typography.Text>
      {action}
    </div>
  );
};

const DashboardPage: React.FC = () => {
  const dashApi = useDashboardApi();
  const school = useSchoolApi();
  const payApi = usePaymentsApi();
  const navigate = useNavigate();

  const { recent } = useRecentStudents();
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [pay, setPay] = useState<PaySummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dashApi.get()
      .then(setSummary)
      .catch(() => setSummary(null))
      .finally(() => setLoading(false));
  }, [dashApi]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ys = await school.listYears();
        if (!ys.length) return;
        const ps = await payApi.listPeriods(ys[0].id);
        const period = pickCurrentPeriod(ps);
        if (!period) return;
        const rows = await payApi.list(period.id);
        if (cancelled) return;
        let expected = 0, collected = 0, unpaidCount = 0, unassigned = 0;
        rows.forEach((r: PaymentRow) => {
          const amount = r.amount_due ?? r.amount_current;
          if (!r.class_label) unassigned += 1;
          if (amount != null) {
            expected += amount;
            if (r.status === "PAYE") collected += amount;
          }
          if (r.status !== "PAYE") unpaidCount += 1;
        });
        setPay({
          label: period.label,
          expected, collected, outstanding: expected - collected,
          rate: expected > 0 ? Math.round((collected / expected) * 100) : 0,
          unpaidCount, unassigned,
        });
      } catch {
        /* 403 (non direction) ou aucune période : la carte se masque */
      }
    })();
    return () => { cancelled = true; };
  }, [school, payApi]);

  const att = summary?.attendance_today;

  // Alertes agrégées des données déjà chargées — chacune pointe vers son écran.
  const alerts: { text: string; to: string }[] = [];
  if (pay && pay.unpaidCount > 0)
    alerts.push({ text: `${pay.unpaidCount} élève(s) à encaisser — ${pay.label}`, to: "/paiements" });
  if (att && att.unjustified_absences > 0)
    alerts.push({ text: `${att.unjustified_absences} absence(s) non justifiée(s) aujourd'hui`, to: "/pedagogie" });
  if (pay && pay.unassigned > 0)
    alerts.push({ text: `${pay.unassigned} élève(s) sans classe (non facturés)`, to: "/eleves" });

  const nextExam = summary?.upcoming_exams[0];

  return (
    <div>
      <PageHeader
        title="Tableau de bord"
        subtitle={`Vue d'ensemble · ${dayjs().format("DD/MM/YYYY")}`}
      />

      {/* Démarrage : ne s'affiche que tant que l'établissement n'est pas
          entièrement configuré, puis s'efface. */}
      <SetupChecklist />

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} xl={6}>
          <StatCard
            icon={<TeamOutlined />}
            label="Élèves actifs"
            tone="info"
            loading={loading}
            value={<CountUp value={summary?.active_students ?? 0} />}
            footer={<Typography.Text type="secondary" style={{ fontSize: 12 }}>Effectif de l'établissement</Typography.Text>}
            to="/eleves"
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StatCard
            icon={<DollarOutlined />}
            label="Reste à encaisser"
            // Rouge et liseré seulement s'il reste quelque chose à encaisser :
            // une caisse à jour n'a pas à crier.
            tone={pay && pay.outstanding > 0 ? "danger" : "success"}
            alert={!!pay && pay.outstanding > 0}
            value={<CountUp value={pay ? pay.outstanding : 0} format={formatAmount} />}
            footer={
              pay ? (
                <>
                  {/* Barre plutôt qu'un cercle de 30px : un taux se lit sur une
                      longueur, pas sur un arc de la taille d'une icône. */}
                  <Progress
                    percent={pay.rate}
                    size="small"
                    showInfo={false}
                    strokeColor={pay.rate === 100 ? COLORS.accent : COLORS.primary}
                    style={{ marginBottom: 2, lineHeight: 1 }}
                  />
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {pay.rate}% recouvré · {pay.label}
                  </Typography.Text>
                </>
              ) : (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Période courante
                </Typography.Text>
              )
            }
            to="/paiements"
            delay={60}
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StatCard
            icon={<WarningOutlined />}
            label="Absents aujourd'hui"
            tone={att && att.absences > 0 ? "danger" : "success"}
            alert={!!att && att.absences > 0}
            loading={loading}
            value={<CountUp value={att?.absences ?? 0} />}
            footer={
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {att?.lates ?? 0} retard(s) · {att?.students_flagged ?? 0} élève(s) concerné(s)
              </Typography.Text>
            }
            to="/pedagogie"
            delay={120}
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StatCard
            icon={<CalendarOutlined />}
            label="Prochaine évaluation"
            tone={nextExam ? "info" : "neutral"}
            loading={loading}
            value={nextExam ? dayjs(nextExam.date).format("DD/MM") : "—"}
            footer={
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {nextExam ? `${nextExam.class_label} · ${nextExam.subject_name}` : "Aucune programmée"}
              </Typography.Text>
            }
            to="/pedagogie"
            delay={180}
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <Card
            className="app-fade-in"
            style={{ animationDelay: "240ms" }}
            title={<Space><CalendarOutlined /> Prochaines évaluations</Space>}
            loading={loading}
            styles={{ body: { paddingTop: summary?.upcoming_exams.length ? 0 : undefined } }}
          >
            {summary && summary.upcoming_exams.length > 0 ? (
              <List
                dataSource={summary.upcoming_exams}
                renderItem={(e) => (
                  // La date passe en vignette : dans un agenda, c'est elle
                  // qu'on cherche en premier, et une ligne de texte la noie.
                  <List.Item className="dash-row" onClick={() => navigate("/pedagogie")}>
                    <List.Item.Meta
                      avatar={
                        <span className="date-chip">
                          <b>{dayjs(e.date).format("DD")}</b>
                          <em>{dayjs(e.date).format("MMM")}</em>
                        </span>
                      }
                      title={
                        <Space size={8}>
                          <span>{e.title}</span>
                          <Tag bordered={false} color={e.kind === "EXAMEN" ? "blue" : "default"}>
                            {KIND_LABEL[e.kind] ?? e.kind}
                          </Tag>
                        </Space>
                      }
                      description={`${e.class_label} · ${e.subject_name}`}
                    />
                    <RightOutlined className="dash-chevron" />
                  </List.Item>
                )}
              />
            ) : (
              <EmptyState
                icon={<CalendarOutlined />}
                text="Aucune évaluation à venir"
                action={
                  <Button type="link" onClick={() => navigate("/pedagogie")}>
                    Programmer une évaluation
                  </Button>
                }
              />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          {/* Reprise de travail : les derniers dossiers ouverts sur ce poste,
              avant les alertes — on revient plus souvent à un dossier en cours
              qu'on ne traite une alerte. */}
          {recent.length > 0 && (
            <Card
              className="app-fade-in"
              style={{ marginBottom: 16, animationDelay: "300ms" }}
              title={<Space><TeamOutlined /> Dossiers récents</Space>}
            >
              <List
                dataSource={recent}
                renderItem={(r) => (
                  <List.Item className="dash-row" onClick={() => navigate(`/students/${r.id}`)}>
                    <Typography.Text>{r.name}</Typography.Text>
                    <RightOutlined className="dash-chevron" />
                  </List.Item>
                )}
              />
            </Card>
          )}
          <Card
            className="app-fade-in"
            style={{ animationDelay: "360ms" }}
            title={<Space><WarningOutlined /> Alertes</Space>}
            loading={loading}
          >
            {alerts.length > 0 ? (
              <List
                dataSource={alerts}
                renderItem={(a) => (
                  // Pastille ambre en tête de ligne : la liste se compte d'un
                  // regard, sans lire chaque phrase.
                  <List.Item className="dash-row" onClick={() => navigate(a.to)}>
                    <Space size={10} align="start">
                      <span className="alert-dot" />
                      <Typography.Text>{a.text}</Typography.Text>
                    </Space>
                    <RightOutlined className="dash-chevron" />
                  </List.Item>
                )}
              />
            ) : (
              // Rien à traiter n'est pas un manque : c'est le bon état, et il
              // se dit en vert plutôt qu'en gris.
              <EmptyState
                icon={<CheckCircleOutlined />}
                tone="success"
                text="Aucune alerte — tout est à jour"
              />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DashboardPage;
